import { permit } from '@lba-dev/package.local-globals/restrictions';
import { R_FOR } from '@lba-dev/package.local-globals/remStatus';
import { A_ARC, A_IAC } from '@lba-dev/package.local-globals/aStatus';
import { AS_INDSP, AS_QUA } from '@lba-dev/package.local-globals/aSubStatus';
import { QUA_MEDIUM, QUA_HIGH } from '@lba-dev/package.local-globals/aLevel';
import {
  DOCUMENT_STATUS
} from '@lba-dev/package.local-globals/artisanDocumentCheck';

export const QUERY_CONTEXTS = {
  DEFAULT: 'default',
  PROPOSITION_ALL: 'propositionAll',
  EXTRA_DATA_FORFAIT: 'extraDataForfait',
  EXTRA_DATA_ARCH: 'extraDataArch',
  PICK_ARTISAN_SAV: 'pickArtisanSav',
};

// Un sous-métier n'est exploitable que s'il a un `id` :
// un objet non vide mais sans `id` (ex: { _id, answersCombo })
// ne doit pas être considéré comme un sous-métier valide
const hasMetierId = (metier) => Boolean(metier?.get?.('id'));

const getMetierRequirements = (merge) => {
  // On prend le premier sous-métier qui a vraiment un `id`
  const rawMetier = [
    merge.get('metiersDevis'),
    merge.get('metiersSteps'),
  ].find(hasMetierId);

  if (!rawMetier) {
    return null;
  }

  return {
    requireRCPro: rawMetier.get('requireRCPro'),
    requireDecennale: rawMetier.get('requireDecennale'),
    requireArtisansPGI: rawMetier.get('requireArtisansPGI')
  };
};

const buildCategoryFilter = (categorie, sousMetier) => {
  // Sans `id`, on filtre par catégorie : sinon { 'sousMetiers.id': undefined }
  // est supprimé par JSON.stringify et la requête n'a plus de filtre métier
  if (hasMetierId(sousMetier)) {
    return { 'sousMetiers.id': sousMetier.get('id') };
  }
  return { categories: { $elemMatch: { $eq: categorie } } };
};

const buildLevelFilter = (merge) => ({
  level: {
    $lt: merge.getIn(['onSitePayment']) ? QUA_MEDIUM : QUA_HIGH
  }
});

const buildDocumentRequirements = (metierRequirements) => {
  if (!metierRequirements) {
    return {};
  }

  const requirements = {};

  if (metierRequirements.requireRCPro) {
    requirements['documentStatus.assuranceRCPro'] = DOCUMENT_STATUS.VALID;
  }

  if (metierRequirements.requireDecennale) {
    requirements['documentStatus.assuranceDecennale'] = DOCUMENT_STATUS.VALID;
  }

  if (metierRequirements.requireArtisansPGI) {
    requirements.PGNumber = { $exists: true, $ne: null };
  }

  return requirements;
};

export const buildArtisanQuery = ({
  categorie,
  merge,
  user,
  context = QUERY_CONTEXTS.DEFAULT,
  filterType = null,
  percent = null,
}) => {
  const sousMetier = merge.get('metiersDevis');
  const metierRequirements = getMetierRequirements(merge);

  const baseQuery = {
    ...buildCategoryFilter(categorie, sousMetier),
    status: { $ne: A_ARC },
  };

  switch (context) {
    case QUERY_CONTEXTS.DEFAULT: {
      const query = {
        ...baseQuery,
        price: merge.getIn(['announcedPrice']),
        dSigned: merge.hasIn(['login', 'devis']),
        onSitePayment: merge.getIn(['onSitePayment']),
      };

      if (!permit(user, { key: 'partenariatIntern' })) {
        Object.assign(query, buildLevelFilter(merge));
        Object.assign(query, buildDocumentRequirements(metierRequirements));
      }

      if (filterType && filterType.filter) {
        Object.assign(query, filterType.filter(percent));
      }

      return query;
    }

    case QUERY_CONTEXTS.PROPOSITION_ALL: {
      const query = {
        ...baseQuery,
        status: { $nin: [A_ARC, A_IAC] },
        price: merge.getIn(['announcedPrice']),
        dSigned: merge.hasIn(['login', 'devis']),
        onSitePayment: merge.getIn(['onSitePayment']),
      };

      Object.assign(query, buildLevelFilter(merge));
      Object.assign(query, buildDocumentRequirements(metierRequirements));

      if (filterType && filterType.filter) {
        Object.assign(query, filterType.filter(percent));
      }

      if (merge.getIn(['combination', 'currentArtisanControle'])) {
        query['remuneration.pourcentage.labor'] = { $lte: 40 };
      }

      return query;
    }
    case QUERY_CONTEXTS.EXTRA_DATA_FORFAIT: {
      const query = {
        categories: { $elemMatch: { $eq: categorie } },
        status: { $ne: A_ARC },
        'remuneration.status': R_FOR,
      };

      if (!permit(user, { key: 'partenariatIntern' })) {
        Object.assign(query, buildLevelFilter(merge));
      }

      return query;
    }

    case QUERY_CONTEXTS.EXTRA_DATA_ARCH: {
      return {
        categories: { $elemMatch: { $eq: categorie } },
        status: A_ARC,
      };
    }

    case QUERY_CONTEXTS.PICK_ARTISAN_SAV: {
      return {
        categories: { $elemMatch: { $eq: categorie } },
        status: { $ne: A_ARC },
        subStatus: { $nin: [AS_INDSP, AS_QUA] }
      };
    }

    default:
      return baseQuery;
  }
};

export const PATHS = {
  fromObj: (p) => ['obj', ...p],
  savDate: ['date', 'savDate'],
  savMaxDate: ['date', 'savMax'],
  interventionDate: ['date', 'intervention'],
  interventionMaxDate: ['infoDesc', 'maxHour'],
  flexHour: ['flexHour'],
  labor: ['remuneration', 'pourcentage', 'labor'],
  remunerationStatus: ['remuneration', 'status'],
  status: ['status'],
  onSitePayment: ['onSitePayment'],
  announcedPrice: ['announcedPrice'],
  currId: ['currentArtisan', 'id'],
  devisSigned: ['login', 'devis'],
  metiersDevis: ['metiersDevis'],
  metiersSteps: ['metiersSteps'],
};
